/**
 * Version polling hook for detecting new deployments
 *
 * Periodically checks /version.json to compare the current git hash
 * against the deployed version. Shows a toast when a new version is available.
 */
import { useCallback, useEffect, useRef } from "react";
import { useToast } from "@/hooks";

// Poll every 60 seconds
const VERSION_POLL_INTERVAL = 60000;

// Get current git hash from build time (injected by Vite)
const CURRENT_GIT_HASH = import.meta.env.VITE_GIT_HASH || null;

export function useVersionPolling() {
  const { toast } = useToast();
  const hasNotified = useRef(false);
  const intervalRef = useRef(null);

  const checkVersion = useCallback(async () => {
    // Skip if no current hash to compare against
    if (!CURRENT_GIT_HASH) return;

    try {
      // Cache-bust the request
      const res = await fetch(`/version.json?t=${Date.now()}`);
      if (!res.ok) return;

      const { gitHash } = await res.json();

      // Check if server has a different version
      if (gitHash && gitHash !== CURRENT_GIT_HASH && !hasNotified.current) {
        hasNotified.current = true;

        toast({
          title: "Update Available",
          description:
            "A new version is available. Refresh to get the latest features.",
          duration: 0, // Don't auto-dismiss
          action: (
            <button
              onClick={() => window.location.reload()}
              className="px-3 py-1.5 text-xs font-medium rounded-md bg-purple-600 text-white hover:bg-purple-700 transition-colors"
            >
              Refresh
            </button>
          ),
        });
      }
    } catch {
      // Silent fail - network issues shouldn't spam errors
    }
  }, [toast]);

  useEffect(() => {
    // Skip in development - version.json won't have accurate git hash
    if (import.meta.env.DEV) return;

    // Initial check after a short delay (let app settle)
    const initialTimeout = setTimeout(checkVersion, 5000);

    // Start polling
    intervalRef.current = setInterval(checkVersion, VERSION_POLL_INTERVAL);

    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkVersion]);
}

export default useVersionPolling;
